"use client";

import { CartesianGrid, Line, LineChart, XAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { useEffect, useState } from "react";
import { BASE_URL } from "@/config";
import axiosInstance from "@/utils/axiosInstance";

const chartConfig = {
  tours: {
    label: "Tours",
    color: "hsl(var(--chart-1))",
  },
  driver_id: {
    label: "Driver",
    color: "hsl(var(--chart-2))",
  },
} satisfies ChartConfig;

const DriverTasksLastMonths = () => {
  const [chartData, setChartData] = useState<
    { month: string; tours: number; driver_id: number }[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axiosInstance.get(
          `reports-taxi/tours-by-driver`,
        );
        
        setChartData(data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getLastSixMonthsRange = () => {
    const now = new Date();
    const months: any[] = [];

    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = new Intl.DateTimeFormat("en-US", {
        month: "long",
      }).format(date);
      const year = date.getFullYear();
      months.push({ name: monthName, year });
    }

    const first = months[0];
    const last = months[5];

    return `${first.name} ${first.year} - ${last.name} ${last.year}`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Driver Assigned Tours - Last 6 Months</CardTitle>
        <CardDescription>{getLastSixMonthsRange()}</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig}>
          <LineChart
            accessibilityLayer
            data={chartData}
            margin={{
              left: 12,
              right: 12,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => value.slice(0, 3)}
            />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel />}
            />
            <Line
              dataKey="count"
              type="natural"
              stroke="#222"
              strokeWidth={2}
              dot={{
                fill: "var(--color-tours)",
              }}
              activeDot={{
                r: 6,
              }}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
      <CardFooter className="flex-col items-start gap-2 text-sm">
        <div className="leading-none text-muted-foreground">
          Showing tours assigned to drivers for the last 6 months
        </div>
      </CardFooter>
    </Card>
  );
};

export default DriverTasksLastMonths;
